import * as Sentry from "@sentry/react";
import { captureReactException, reactErrorHandler } from "@sentry/react";
import { ENV } from "./env";
import LOCAL_STORAGE from "@utils/localStorage";

function getQueryParam(name) {
  try {
    const u = new URL(window.location.href);
    let v = u.searchParams.get(name);
    if (v !== null && v !== "") return v;
    const hash = u.hash || "";
    const q = hash.indexOf("?");
    if (q === -1) return null;
    v = new URLSearchParams(hash.slice(q + 1)).get(name);
    if (v !== null && v !== "") return v;
  } catch (_) {}
  return null;
}

function getSentrySelfTestMode() {
  const v = getQueryParam("sentry_self_test");
  if (v === "1" || v === "auto" || v === "full") return v;
  return null;
}

function sendIntentionalTestError(reason) {
  try {
    Sentry.captureException(
      new Error(
        `TCTV_INTENTIONAL_SENTRY_TEST_ERROR (${reason})`
      ),
      {
        tags: { sentry_test: "intentional" },
        fingerprint: ["tctv-sentry-smoke-test"],
      }
    );
    void Sentry.flush(2000);
  } catch (e) {
    console.warn("[Sentry] sendIntentionalTestError failed", e);
  }
}

function runIntentionalSentryTestError() {
  if (getQueryParam("sentry_test_error") !== "1") return;
  setTimeout(() => sendIntentionalTestError("url_param"), 100);
}

function exposeConsoleSentryTest() {
  window.__TCTV_SENTRY_SEND_TEST__ = () => {
    sendIntentionalTestError("console");
    return "capture queued — check Network for ingest.de.sentry.io and Sentry Issues for TCTV_INTENTIONAL";
  };
}

function registerSentrySelfTest() {
  window.__sentrySelfTest = {
    manualCapture: () =>
      Sentry.captureException(
        new Error("[Sentry self-test] manual captureException")
      ),
    syncThrow: () => {
      throw new Error("[Sentry self-test] synchronous throw");
    },
    asyncThrow: () => {
      setTimeout(() => {
        throw new Error("[Sentry self-test] setTimeout throw");
      }, 0);
    },
    promiseReject: () => {
      Promise.reject(new Error("[Sentry self-test] unhandled rejection"));
    },
    shouldBeFiltered: () => {
      throw new Error("ResizeObserver loop limit exceeded");
    },
    runSequence: () => {
      Sentry.captureException(
        new Error("[Sentry self-test] runSequence step 1 manual")
      );
      setTimeout(() => {
        setTimeout(() => {
          throw new Error("[Sentry self-test] runSequence step 2 async");
        }, 0);
      }, 1000);
      setTimeout(() => {
        Promise.reject(
          new Error("[Sentry self-test] runSequence step 3 rejection")
        );
      }, 2500);
    },
  };
  console.info(
    "[Sentry self-test] window.__sentrySelfTest — manualCapture(), syncThrow(), asyncThrow(), promiseReject(), shouldBeFiltered() (must not ingest), runSequence()"
  );
}

export const syncSentryDeviceContext = () => {
  if (!ENV.SENTRY_DSN) return;
  const mac = LOCAL_STORAGE.MAC_ADDRESS.GET();
  if (mac) {
    Sentry.setTag("mac_address", mac);
    Sentry.setContext("tv_device", { mac_address: mac });
  }
};

export const initSentry = () => {
  if (!ENV.SENTRY_DSN) {
    console.warn("Sentry DSN not configured, error tracking disabled");
    return;
  }

  const sentrySelfTest = getSentrySelfTestMode();
  const wantsSentryTestError = getQueryParam("sentry_test_error") === "1";
  const wantsSentryDebug =
    getQueryParam("sentry_debug") === "1" ||
    (typeof localStorage !== "undefined" &&
      localStorage.getItem("sentry_debug") === "1");

  const fullTracingWhileDebugging =
    Boolean(sentrySelfTest || wantsSentryTestError || wantsSentryDebug);

  Sentry.init({
    dsn: ENV.SENTRY_DSN,
    environment: ENV.SENTRY_ENVIRONMENT || "production",
    skipBrowserExtensionCheck: true,
    debug: Boolean(sentrySelfTest || wantsSentryTestError || wantsSentryDebug),
    release: "1.6.31",

    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({
        maskAllText: false,
        blockAllMedia: false,
      }),
    ],

    tracesSampleRate: fullTracingWhileDebugging ? 1 : 0.1,
    replaysSessionSampleRate: 0.1,
    replaysOnErrorSampleRate: 1.0,

    beforeSend(event, hint) {
      const error = hint.originalException;

      if (error && error.message) {
        const ignoredPatterns = [
          "ResizeObserver",
          "Script error",
          "destroyPlayer is not a function",
          "getModel is not a function",
          "goToHome is not a function",
        ];
        if (ignoredPatterns.some((p) => error.message.includes(p))) {
          return null;
        }
      }

      try {
        const mac = localStorage.getItem("mac_address");
        event.tags = { ...event.tags, mac_address: mac || "unknown" };
      } catch (_) {}

      return event;
    },

    initialScope: {
      tags: {
        platform: window.Android ? "android_tv" : "web",
        device_os: localStorage.getItem("device_os") || "unknown",
      },
    },
  });

  Sentry.setUser({
    id: localStorage.getItem("mac_address") || "unknown",
  });
  syncSentryDeviceContext();
  exposeConsoleSentryTest();
  runIntentionalSentryTestError();

  if (sentrySelfTest) {
    registerSentrySelfTest();
    if (sentrySelfTest === "auto") {
      setTimeout(() => {
        Sentry.captureException(
          new Error("[Sentry self-test] automatic capture on load")
        );
      }, 1500);
    }
    if (sentrySelfTest === "full") {
      setTimeout(() => {
        window.__sentrySelfTest?.runSequence?.();
      }, 1500);
    }
  }
};

export const setUserContext = (userData) => {
  Sentry.setUser({
    id: userData.id || localStorage.getItem("mac_address"),
    ...userData,
  });
  syncSentryDeviceContext();
};

export const captureError = (error, context = {}) => {
  Sentry.captureException(error, {
    extra: context,
  });
};

export const captureReactBoundaryError = (error, errorInfo) => {
  captureReactException(error, errorInfo);
  void Sentry.flush(2000);
};

export const captureMessage = (message, level = "info", context = {}) => {
  Sentry.captureMessage(message, {
    level,
    extra: context,
  });
};

export const addBreadcrumb = (message, category, data = {}) => {
  Sentry.addBreadcrumb({
    message,
    category,
    data,
    level: "info",
  });
};

export const SentryErrorBoundary = Sentry.ErrorBoundary;

export { Sentry, reactErrorHandler };
